import { resetRouter } from '@/router';
import store from '@/shared/config/store';
import { TagsViewStoreModule as tagsViewStore } from "@/shared/config/store/tags-view-store";
import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import axios from 'axios';
import { AppStoreModule } from './app-store';

export interface IAccountState {
  logon: boolean;
  userIdentity: object;
  userDetails: object;
  authenticated: boolean;
  ribbonOnProfiles: string;
  activeProfiles: Array<string>;
  authorities: Set<string>;
  grantedDocActions: Map<number, Set<number>>;
  grantedDashboards: Map<string, Set<number>>;
  grantedOrgs: Map<number, any>;
  selectedRoleId: number;
  selectedOrgId: number;
}

@Module({ dynamic: true, store, name: 'accountStore', namespaced: true })
class AccountStore extends VuexModule implements IAccountState {
  public logon = false;
  public userIdentity = null;
  public userDetails = null;
  public authenticated = false;
  public ribbonOnProfiles = '';
  public activeProfiles = [];
  public authorities = new Set<string>();
  public grantedDocActions = new Map<number, Set<number>>();
  public grantedDashboards = new Map<string, Set<number>>();
  public grantedOrgs = new Map<number, any>();
  public properties = new Map<string, any>();
  public selectedRoleId = null;
  public selectedOrgId = null;

  public get account() {
    return this.userIdentity;
  }

  public get isEmployee() {
    return this.userDetails.employee;
  }

  public get isVendor() {
    return this.userDetails.vendor;
  }

  public get organizationInfo() {
    return {
      id: this.userDetails.adOrganizationId,
      name: this.userDetails.adOrganizationName
    };
  }

  public get property() {
    return (key: string) => {
      const value = this.properties.get(key);
      if (typeof value === 'function') {
        return value();
      }
      return value;
    };
  }

  public get vendorInfo() {
    if (! this.isVendor) {
      return {};
    }
    
    return {
      id: this.userDetails.cVendorId,
      name: this.userDetails.cVendorName
    };
  }

  @Mutation
  private INIT_AUTHENTICATION() {
    this.logon = true;
  }

  @Mutation
  private SET_AUTHENTICATED(identity: any) {
    this.userIdentity = identity;
    this.authorities = new Set(identity.authorities);
    this.authenticated = true;
    this.logon = false;
  }

  @Mutation
  private SET_USER_DETAILS(user: any) {
    this.userDetails = user;
    this.properties.set('adOrganizationId', user.adOrganizationId);
    this.properties.set('adOrganizationName', user.adOrganizationName);
    this.properties.set('email', user.email);
    this.properties.set('isVendor', user.vendor);
    this.properties.set('userId', user.id);
    this.properties.set('userLogin', user.userLogin);
    this.properties.set('vendorId', user.cVendorId);
    this.properties.set('vendorName', user.cVendorName);
    this.properties.set('date', () => new Date());
  }

  @Mutation
  private SET_GRANTED_RESOURCES(accesses: Array<any>) {
    for (const access of accesses) {
      // Grant access to document actions.
      if (access.accessType === 'DOC_ACTION') {
        let docActions = this.grantedDocActions.get(access.documentTypeId);

        if (docActions === void 0) {
          docActions = new Set<number>();
          this.grantedDocActions.set(access.documentTypeId, docActions);
        }
        docActions.add(access.referenceListId);
      }
      
      // Grant access to dashboards.
      else if (access.accessType === 'DASHBOARD') {
        const dashboardKey = `${access.paDashboardId}_${access.paDashboardName}`;
        let dashboardItems = this.grantedDashboards.get(dashboardKey);

        if (dashboardItems === void 0) {
          dashboardItems = new Set<number>();
          this.grantedDashboards.set(dashboardKey, dashboardItems);
        }
        dashboardItems.add(access.paDashboardItemId);
      }

      else if (access.accessType === 'ORG') {
        this.grantedOrgs.set(access.adOrganizationId, access);
      }
    }
  }

  @Mutation
  private SET_LOGOUT() {
    resetRouter();
    AppStoreModule.setSelectRole(false);

    if (this.userDetails) {
      axios.delete(`/api/t-reservations/${this.userDetails.userLogin}/release`)
        .finally(() => {
          localStorage.removeItem('jhi-authenticationToken');
          sessionStorage.removeItem('jhi-authenticationToken');
        });
    }

    tagsViewStore.delAllViews();
    this.userIdentity = null;
    this.userDetails = null;
    this.authorities.clear();
    this.authenticated = false;
    this.logon = false;
    this.grantedDashboards.clear();
    this.grantedDocActions.clear();
    this.grantedOrgs.clear();
    this.properties.clear();
  }

  @Mutation
  private SET_PROFILES(profiles: Array<string>) {
    this.activeProfiles = profiles;
  }

  @Mutation
  private SET_RIBBON(ribbon: string) {
    this.ribbonOnProfiles = ribbon;
  }

  @Mutation
  private SET_CURRENT_ORG(data: any) {
    this.selectedRoleId = data.roleId;
    this.selectedOrgId = data.orgId;

    const orgName = this.grantedOrgs.get(data.orgId)?.adOrganizationName;

    this.userDetails = {
      ...this.userDetails,
      ...{
        adOrganizationId: data.orgId,
        adOrganizationName: orgName
      }
    };

    this.properties.set('adOrganizationId', data.orgId);
    this.properties.set('adOrganizationName', orgName);
    localStorage.setItem('opus-default-org', String(this.selectedOrgId));
  }

  @Action
  public async authenticate() {
    this.INIT_AUTHENTICATION();
  }

  @Action
  public async setAuthenticated(identity: object) {
    this.SET_AUTHENTICATED(identity);
  }

  @Action
  public async setUserDetails(user: any) {
    this.SET_USER_DETAILS(user);
  }

  @Action
  public async setGrantedResources(accesses: Array<any>) {
    this.SET_GRANTED_RESOURCES(accesses);
  }

  @Action
  public async logout() {
    this.SET_LOGOUT();
  }

  @Action
  public async setActiveProfiles(profiles: Array<string>) {
    this.SET_PROFILES(profiles);
  }

  @Action
  public async setRibbonOnProfiles(ribbon: string) {
    this.SET_RIBBON(ribbon);
  }

  @Action
  public async setCurrentOrg(data: any) {
    this.SET_CURRENT_ORG(data);
  }
}

export const AccountStoreModule = getModule(AccountStore);
